import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../auth/AuthContext';
import { HostsTab } from './config/HostsTab';
import { ServicesTab } from './config/ServicesTab';
import { RulesTab } from './config/RulesTab';
import { TargetsTab } from './config/TargetsTab';
import { SecretsTab } from './config/SecretsTab';
import { UsersTab } from './config/UsersTab';
import { SessionsTab } from './config/SessionsTab';
import { DevicesTab } from './config/DevicesTab';

interface TabDef {
  id: string;
  label: string;
  el: React.ReactNode;
  adminOnly?: boolean;
}

const ALL_TABS: TabDef[] = [
  { id: 'hosts', label: 'Hosts', el: <HostsTab /> },
  { id: 'services', label: 'Services', el: <ServicesTab /> },
  { id: 'rules', label: 'Alert rules', el: <RulesTab /> },
  { id: 'targets', label: 'Targets', el: <TargetsTab /> },
  { id: 'secrets', label: 'Secrets', el: <SecretsTab />, adminOnly: true },
  { id: 'users', label: 'Users', el: <UsersTab />, adminOnly: true },
  { id: 'sessions', label: 'Sessions', el: <SessionsTab />, adminOnly: true },
  { id: 'devices', label: 'Devices', el: <DevicesTab />, adminOnly: true },
];

export function ConfigPage() {
  const { session } = useAuth();
  const isAdmin = session.user?.role === 'admin';

  const tabs = useMemo(() => ALL_TABS.filter((t) => !t.adminOnly || isAdmin), [isAdmin]);

  const [tab, setTab] = useState<string>(() => {
    const hash = window.location.hash.replace(/^#/, '');
    return tabs.find((t) => t.id === hash)?.id ?? tabs[0]?.id ?? 'hosts';
  });

  function selectTab(id: string) {
    setTab(id);
    history.replaceState(null, '', `${window.location.pathname}#${id}`);
  }

  const current = tabs.find((t) => t.id === tab) ?? tabs[0];

  return (
    <div className="min-h-screen bg-cockpit-bg text-cockpit-text">
      <header className="border-b border-cockpit-border bg-cockpit-bg/95 backdrop-blur">
        <div className="mx-auto flex max-w-7xl items-center gap-4 px-6 py-3">
          <Link to="/" className="text-cockpit-dim hover:text-cockpit-text">
            ← Dashboard
          </Link>
          <h1 className="text-lg font-semibold">Configuration</h1>
        </div>
        <nav className="border-t border-cockpit-border">
          <div className="mx-auto max-w-7xl px-6">
            <ul className="flex gap-1">
              {tabs.map((t) => (
                <li key={t.id}>
                  <button
                    type="button"
                    onClick={() => selectTab(t.id)}
                    className={
                      tab === t.id
                        ? 'border-b-2 border-cockpit-text px-3 py-2 text-sm font-medium text-cockpit-text'
                        : 'border-b-2 border-transparent px-3 py-2 text-sm text-cockpit-dim hover:text-cockpit-text'
                    }
                  >
                    {t.label}
                  </button>
                </li>
              ))}
            </ul>
          </div>
        </nav>
      </header>

      <main className="mx-auto max-w-7xl space-y-6 px-6 py-6">{current?.el}</main>
    </div>
  );
}
